import React, { useState } from 'react';
import { Mail, Target, Eye, TrendingUp, Users, ArrowRight, CheckCircle2 } from 'lucide-react';
import ContactForm from './ContactForm';
import './LandingPage.css';

const LandingPage = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      
      {/* Header Navigation */}
      <header className="header-nav" role="banner">
        <div className="container">
          <div className="nav-content">
            <div className="logo-text" aria-label="Praxis AI Home">Praxis AI</div>
            <button 
              className="btn-primary" 
              onClick={scrollToContact}
              aria-label="Request pilot access for your MBA program"
            >
              Request Pilot Access
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" aria-labelledby="hero-heading">
        <div className="container">
          <div className="hero-content fade-in-up">
            <div className="hero-announcement">
              <span className="announcement-badge">For MBA Programs</span>
              <span>Powered by AI</span>
            </div>
            <h1 id="hero-heading" className="heading-hero">
              Turn Real Customer Workflows Into Startup Insight
            </h1>
            <p className="hero-subtitle body-large">
              An AI agent that learns directly from how your customers work—so you don't have to be the domain expert.
            </p>
            <div className="hero-cta-group">
              <button 
                className="btn-primary" 
                onClick={scrollToContact}
                aria-label="See how Praxis AI works"
              >
                See How It Works
              </button>
              <button 
                className="btn-secondary" 
                onClick={scrollToContact}
                aria-label="Request a demo of Praxis AI"
              >
                Request Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section id="main-content" className="problem-section section-padding" aria-labelledby="problem-heading">
        <div className="container">
          <div className="section-header">
            <h2 id="problem-heading" className="heading-1">The Challenge for MBA Entrepreneurs</h2>
            <p className="body-large section-subtitle">
              Building startups without deep subject-matter expertise
            </p>
          </div>
          
          <div className="problem-grid">
            <div className="problem-card hover-lift">
              <div className="problem-icon">
                <Target size={32} />
              </div>
              <h3 className="heading-3">Limited Domain Knowledge</h3>
              <p className="body-medium">
                MBA students are expected to identify opportunities and build solutions in industries they've never worked in.
              </p>
            </div>
            
            <div className="problem-card hover-lift">
              <div className="problem-icon">
                <Eye size={32} />
              </div>
              <h3 className="heading-3">Surface-Level Insights</h3>
              <p className="body-medium">
                Interviews and surveys only capture what customers think they do—not their actual workflows and pain points.
              </p>
            </div>
            
            <div className="problem-card hover-lift">
              <div className="problem-icon">
                <TrendingUp size={32} />
              </div>
              <h3 className="heading-3">Slow Validation Cycles</h3>
              <p className="body-medium">
                Traditional customer discovery is time-intensive and often based on incomplete or biased information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">How Praxis AI Works</h2>
            <p className="body-large section-subtitle">
              Observe real workflows, identify real opportunities
            </p>
          </div>
          
          <div className="steps-container">
            <div className="step-card">
              <div className="step-visual">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHx3ZWIlMjBicm93c2VyJTIwZGFzaGJvYXJkJTIwYW5hbHl0aWNzJTIwbW9uaXRvcmluZyUyMHNjcmVlbnxlbnwwfHx8fDE3NzE0Mzc5Nzh8MA&ixlib=rb-4.1.0&q=85&w=400" 
                  alt="Performance analytics dashboard showing workflow monitoring graphs and real-time metrics"
                  className="step-image"
                  loading="lazy"
                />
                <div className="step-number">01</div>
              </div>
              <div className="step-content">
                <h3 className="heading-3">Browser Extension Observes</h3>
                <p className="body-medium">
                  A lightweight browser extension quietly observes how users actually work—capturing tools, workflows, and decision points in real-time.
                </p>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-visual">
                <img 
                  src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxkYXRhJTIwYW5hbHlzaXN8ZW58MHx8fHwxNzcxMzUyNTQ5fDA&ixlib=rb-4.1.0&q=85&w=400" 
                  alt="AI-powered data visualization showing colorful 3D patterns and analytics identifying workflow friction points"
                  className="step-image"
                  loading="lazy"
                />
                <div className="step-number">02</div>
              </div>
              <div className="step-content">
                <h3 className="heading-3">AI Identifies Patterns</h3>
                <p className="body-medium">
                  Our AI agent analyzes workflow data to identify friction points, tool switching, repetitive tasks, and optimization opportunities.
                </p>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-visual">
                <img 
                  src="https://images.unsplash.com/photo-1543286386-2e659306cd6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxncm93dGglMjBjaGFydHxlbnwwfHx8fDE3NzEzNTI1NTJ8MA&ixlib=rb-4.1.0&q=85&w=400" 
                  alt="Hand-drawn growth chart with upward trend showing continuous learning and business improvement over time"
                  className="step-image"
                  loading="lazy"
                />
                <div className="step-number">03</div>
              </div>
              <div className="step-content">
                <h3 className="heading-3">Continuous Learning</h3>
                <p className="body-medium">
                  The system learns continuously as more workflow data is captured, surfacing deeper insights and business opportunities over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="why-different-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Why Praxis AI Is Different</h2>
            <p className="body-large section-subtitle">
              Behavior-based insights, not self-reported opinions
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card accent-purple">
              <CheckCircle2 className="feature-icon" size={28} />
              <h3 className="heading-3">Customer-Generated Expertise</h3>
              <p className="body-medium">
                Let your customers' actual behaviors generate the domain expertise you need—no consulting required.
              </p>
            </div>
            
            <div className="feature-card accent-blue">
              <CheckCircle2 className="feature-icon" size={28} />
              <h3 className="heading-3">Behavior-Based Data</h3>
              <p className="body-medium">
                See what customers actually do, not what they say they do—eliminating bias and uncovering hidden needs.
              </p>
            </div>
            
            <div className="feature-card accent-orange">
              <CheckCircle2 className="feature-icon" size={28} />
              <h3 className="heading-3">Continuous Improvement</h3>
              <p className="body-medium">
                Insights get richer and more actionable as the AI observes more workflows and identifies patterns at scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MBA Use Cases Section */}
      <section className="mba-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Perfect for MBA Entrepreneurship Programs</h2>
            <p className="body-large section-subtitle">
              Real-world learning meets cutting-edge technology
            </p>
          </div>
          
          <div className="use-case-grid">
            <div className="use-case-card hover-lift">
              <div className="use-case-icon">
                <Users size={36} />
              </div>
              <h3 className="heading-3">Customer Discovery at Scale</h3>
              <p className="body-medium">
                Enable students to observe and learn from real customer workflows across diverse industries and use cases.
              </p>
            </div>
            
            <div className="use-case-card hover-lift">
              <div className="use-case-icon">
                <TrendingUp size={36} />
              </div>
              <h3 className="heading-3">Faster Validation</h3>
              <p className="body-medium">
                Accelerate the lean startup cycle with continuous, behavior-based validation of business hypotheses.
              </p>
            </div>
            
            <div className="use-case-card hover-lift">
              <div className="use-case-icon">
                <Target size={36} />
              </div>
              <h3 className="heading-3">Evidence-Backed Decisions</h3>
              <p className="body-medium">
                Build stronger pitches, business plans, and go-to-market strategies grounded in real workflow data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="contact-section section-padding">
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-header">
              <h2 className="heading-1">Ready to Transform Your Entrepreneurship Program?</h2>
              <p className="body-large">
                Request pilot access for your MBA cohort or schedule a demo to see Praxis AI in action.
              </p>
            </div>
            
            <div className="contact-form-container">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo-text">Praxis AI</div>
              <p className="body-small">
                Turning customer workflows into startup insights
              </p>
            </div>
            <div className="footer-info">
              <p className="body-small">
                © 2026 Praxis AI. Built for MBA entrepreneurship programs.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
